export type HistoryAssignmentRow = {
  id: string;
  assigned_date: string;
  assignment_type: string;
  status: string;
  updated_at: string;
};

export type HistoryAssignmentItemRow = {
  id: string;
  assignment_id: string;
  sequence: number;
};

export type HistorySessionRow = {
  id: string;
  assignment_id: string | null;
  started_at: string;
  ended_at: string | null;
  active_duration_ms: number;
  status: string;
};

export type HistoryAttemptRow = {
  id: string;
  assignment_item_id: string;
  learning_session_id: string | null;
  status: string;
  started_at: string;
  submitted_at: string | null;
  active_duration_ms: number;
};

export type HistoryAnswerRow = {
  id: string;
  attempt_id: string;
  first_attempt_correct: boolean | null;
  final_correct: boolean | null;
  hint_count: number;
  active_duration_ms: number;
  answered_at: string | null;
};

export type LearningSessionSummary = {
  sessionId: string;
  assignmentId: string | null;
  learningDate: string;
  assignmentType: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  activeDurationMs: number;
  answerCount: number;
  firstPassCorrect: number;
  finalCorrect: number;
  correctionNeeded: number;
  hintCount: number;
  answerIds: string[];
};

export function buildLearningSessionSummaries(input: {
  assignments: HistoryAssignmentRow[];
  assignmentItems: HistoryAssignmentItemRow[];
  sessions: HistorySessionRow[];
  attempts: HistoryAttemptRow[];
  answers: HistoryAnswerRow[];
}): LearningSessionSummary[] {
  const assignmentMap = new Map(input.assignments.map((row) => [row.id, row]));
  const itemMap = new Map(input.assignmentItems.map((row) => [row.id, row]));
  const attemptsBySession = new Map<string, HistoryAttemptRow[]>();
  for (const attempt of input.attempts) {
    if (!attempt.learning_session_id) continue;
    const rows = attemptsBySession.get(attempt.learning_session_id) ?? [];
    rows.push(attempt);
    attemptsBySession.set(attempt.learning_session_id, rows);
  }
  const answersByAttempt = new Map<string, HistoryAnswerRow[]>();
  for (const answer of input.answers) {
    const rows = answersByAttempt.get(answer.attempt_id) ?? [];
    rows.push(answer);
    answersByAttempt.set(answer.attempt_id, rows);
  }

  return input.sessions.map((session) => {
    const sessionAttempts = attemptsBySession.get(session.id) ?? [];
    const sessionAnswers = sessionAttempts.flatMap((attempt) => answersByAttempt.get(attempt.id) ?? []);
    const inferredAssignmentId = session.assignment_id
      ?? sessionAttempts.map((attempt) => itemMap.get(attempt.assignment_item_id)?.assignment_id).find(Boolean)
      ?? null;
    const assignment = inferredAssignmentId ? assignmentMap.get(inferredAssignmentId) : undefined;
    return {
      sessionId: session.id,
      assignmentId: inferredAssignmentId,
      learningDate: assignment?.assigned_date ?? session.started_at.slice(0, 10),
      assignmentType: assignment?.assignment_type ?? 'practice',
      status: session.status,
      startedAt: session.started_at,
      endedAt: session.ended_at,
      activeDurationMs: session.active_duration_ms,
      answerCount: sessionAnswers.length,
      firstPassCorrect: sessionAnswers.filter((row) => row.first_attempt_correct === true).length,
      finalCorrect: sessionAnswers.filter((row) => row.final_correct === true).length,
      correctionNeeded: sessionAnswers.filter((row) => row.first_attempt_correct === false && row.final_correct === true).length,
      hintCount: sessionAnswers.reduce((sum, row) => sum + Number(row.hint_count || 0), 0),
      answerIds: sessionAnswers.map((row) => row.id),
    };
  }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
}

export function assignmentTypeLabel(value: string) {
  if (value === 'learn') return 'Belajar konsep';
  if (value === 'review') return 'Ulang kembali';
  if (value === 'remedial') return 'Penguatan';
  if (value === 'mastery_check') return 'Cek kemampuan';
  if (value === 'assessment') return 'Cek hasil belajar';
  if (value === 'teacher_assigned') return 'Dari tutor';
  return 'Latihan';
}
